import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';

/**
 * Operador exibido no filtro.
 */
export interface OperatorAvatarFilterOperator {
  id: string;
  name: string;
  avatar?: string;
}

export interface OperatorAvatarFilterProps {
  /** Lista de operadores. */
  operators: OperatorAvatarFilterOperator[];
  /** ID do operador selecionado (`null` = todos). */
  selectedId: string | null;
  /** Seleção single: recebe o id do operador ou `null` (= todos). */
  onSelect: (id: string | null) => void;
  /** Avatares visíveis antes do "+N" (default 4). */
  maxVisible?: number;
  /** Skeleton de carregamento. */
  loading?: boolean;
  'data-testid'?: string;
}

/** Fallback de avatar sem foto — SEMPRE slate (regra do projeto, ver avatar.tsx). */
const SLATE = '#64748b';
/** Squircle padrão dos avatares (decisão F12: 36px squircle em TUDO). */
const SQUIRCLE = '32%';

const initialsOf = (name: string): string =>
  name ? name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() : '?';

/** Avatar squircle: iniciais slate por baixo + foto por cima (some se a foto falhar). */
const OperatorAvatar: React.FC<{
  operator: OperatorAvatarFilterOperator;
  sizeClass: string;
  fontSize: number;
}> = ({ operator, sizeClass, fontSize }) => (
  <span
    className={`relative ${sizeClass} flex-none overflow-hidden flex items-center justify-center font-semibold text-white`}
    style={{ borderRadius: SQUIRCLE, backgroundColor: SLATE, fontSize }}
  >
    {initialsOf(operator.name)}
    {operator.avatar && (
      <img
        src={operator.avatar}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    )}
  </span>
);

/**
 * OperatorAvatarFilter — filtro de operadores padronizado (findings F12-F14):
 *
 * - Desktop (≥ sm): facepile de avatares **36×36 squircle** (borderRadius 32%,
 *   fallback slate), "Todos" = botão de **ÍCONE** `users` 36×36 squircle (sel =
 *   roxo sólido), selecionado = anel `boxShadow` roxo, "+N" SEMPRE clicável
 *   abrindo dropdown portal com o restante.
 * - Mobile (< sm): chip "Operadores" com badge que abre **bottom-sheet**
 *   (anatomia do MobileOperatorsChip: grab handle, header, linhas avatar+check).
 *
 * Single-select: `selectedId: string | null` (`null` = todos).
 *
 * @example
 * ```tsx
 * <OperatorAvatarFilter
 *   operators={users.map((u) => ({ id: u.id, name: u.name, avatar: u.picture }))}
 *   selectedId={selectedOperatorId}
 *   onSelect={setSelectedOperatorId}
 * />
 * ```
 */
export const OperatorAvatarFilter: React.FC<OperatorAvatarFilterProps> = ({
  operators,
  selectedId,
  onSelect,
  maxVisible = 4,
  loading = false,
  'data-testid': dataTestId,
}) => {
  const testId = dataTestId ?? 'operator-filter';

  const [tooltip, setTooltip] = useState<{ label: string; top: number; left: number } | null>(null);
  const [showOverflow, setShowOverflow] = useState(false);
  const [overflowPosition, setOverflowPosition] = useState({ top: 0, left: 0 });
  const overflowBtnRef = useRef<HTMLButtonElement>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Esc fecha dropdown de overflow e bottom-sheet
  useEffect(() => {
    if (!showOverflow && !sheetOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setShowOverflow(false); setSheetOpen(false); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showOverflow, sheetOpen]);

  const visible = operators.slice(0, maxVisible);
  const overflow = operators.slice(maxVisible);
  const selected = operators.find((op) => op.id === selectedId);

  const showTooltip = (e: React.MouseEvent, label: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ label, top: rect.bottom + 8, left: rect.left + rect.width / 2 });
  };
  const hideTooltip = () => setTooltip(null);

  const pick = (id: string | null) => {
    hideTooltip();
    setShowOverflow(false);
    setSheetOpen(false);
    onSelect(id);
  };

  return (
    <>
      {/* ============ Desktop (≥ sm): facepile 36px squircle ============ */}
      <div className="hidden sm:flex items-center gap-2 flex-none" data-testid={testId}>
        {loading && (
          <>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="w-9 h-9 animate-pulse"
                style={{ borderRadius: SQUIRCLE, backgroundColor: 'var(--theme-border)' }}
              />
            ))}
          </>
        )}

        {!loading && (
          <>
            {/* "Todos" = ÍCONE users 36px squircle (decisão F12); sel = roxo sólido */}
            <button
              type="button"
              onClick={() => pick(null)}
              className="relative w-9 h-9 flex-none flex items-center justify-center transition-transform hover:-translate-y-px"
              style={{
                borderRadius: SQUIRCLE,
                backgroundColor: selectedId === null ? 'var(--theme-primary, #8b5cf6)' : 'transparent',
                color: selectedId === null ? '#ffffff' : 'var(--theme-muted-text)',
              }}
              onMouseEnter={(e) => showTooltip(e, 'Todos os operadores')}
              onMouseLeave={hideTooltip}
              aria-label="Todos os operadores"
              aria-pressed={selectedId === null}
              data-testid={`${testId}-all`}
            >
              <Icon name="users" className="h-4 w-4" />
            </button>

            {/* Avatares visíveis; sel = anel roxo (boxShadow) */}
            {visible.map((op) => (
              <button
                key={op.id}
                type="button"
                onClick={() => pick(op.id)}
                className="relative w-9 h-9 flex-none transition-transform hover:-translate-y-px"
                style={{
                  borderRadius: SQUIRCLE,
                  boxShadow: selectedId === op.id ? '0 0 0 2px var(--theme-primary, #8b5cf6)' : 'none',
                }}
                onMouseEnter={(e) => showTooltip(e, op.name)}
                onMouseLeave={hideTooltip}
                aria-pressed={selectedId === op.id}
                data-testid={`${testId}-${op.id}`}
              >
                <OperatorAvatar operator={op} sizeClass="w-9 h-9" fontSize={12} />
              </button>
            ))}

            {/* "+N" — SEMPRE clicável, abre dropdown com o restante (decisão F13) */}
            {overflow.length > 0 && (
              <button
                ref={overflowBtnRef}
                type="button"
                className="w-9 h-9 flex items-center justify-center text-xs font-semibold flex-none transition-transform hover:-translate-y-px"
                style={{
                  borderRadius: SQUIRCLE,
                  backgroundColor: showOverflow ? 'var(--theme-button-ghost-hover)' : 'transparent',
                  color: 'var(--theme-muted-text)',
                }}
                onClick={() => {
                  if (!showOverflow && overflowBtnRef.current) {
                    const rect = overflowBtnRef.current.getBoundingClientRect();
                    setOverflowPosition({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
                  }
                  setShowOverflow(!showOverflow);
                  hideTooltip();
                }}
                onMouseEnter={(e) => !showOverflow && showTooltip(e, `Mais ${overflow.length} operadores`)}
                onMouseLeave={hideTooltip}
                aria-expanded={showOverflow}
                data-testid={`${testId}-more`}
              >
                +{overflow.length}
              </button>
            )}
          </>
        )}
      </div>

      {/* Dropdown portal do "+N" — tema claro fixo (regra de overlays) */}
      {showOverflow && createPortal(
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setShowOverflow(false)} />
          <div
            className="fixed z-[100]"
            style={{
              top: overflowPosition.top,
              left: overflowPosition.left,
              transform: 'translateX(-50%)',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: '4px 0',
              minWidth: 200,
              maxHeight: 240,
              overflowY: 'auto',
            }}
            data-testid={`${testId}-more-dropdown`}
          >
            {overflow.map((op) => (
              <button
                key={op.id}
                type="button"
                className="w-full flex items-center gap-3 px-3 py-2 transition-colors"
                style={{ backgroundColor: selectedId === op.id ? '#f1f5f9' : 'transparent', color: '#1e293b' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = selectedId === op.id ? '#f1f5f9' : 'transparent'; }}
                onClick={() => pick(op.id)}
                data-testid={`${testId}-more-${op.id}`}
              >
                <OperatorAvatar operator={op} sizeClass="w-7 h-7" fontSize={11} />
                <span className="text-sm font-medium truncate flex-1 text-left">{op.name}</span>
                {selectedId === op.id && (
                  <Icon name="check" className="h-4 w-4 flex-shrink-0" style={{ color: '#8b5cf6' }} />
                )}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}

      {/* Tooltip portal (abaixo do avatar, seta em cima) */}
      {tooltip && createPortal(
        <div
          className="fixed z-[110] pointer-events-none flex flex-col items-center"
          style={{ top: tooltip.top, left: tooltip.left, transform: 'translateX(-50%)' }}
        >
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '6px solid var(--theme-sidebar-active)',
            }}
          />
          <div
            className="whitespace-nowrap rounded-md font-medium"
            style={{
              backgroundColor: 'var(--theme-sidebar-active)',
              color: '#ffffff',
              padding: '6px 14px',
              fontSize: 13,
            }}
          >
            {tooltip.label}
          </div>
        </div>,
        document.body
      )}

      {/* ============ Mobile (< sm): chip "Operadores" → bottom-sheet (decisão F14) ============ */}
      <div className="sm:hidden flex-none">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-full text-[13px] font-medium whitespace-nowrap transition-colors"
          style={{
            border: '1px solid var(--theme-input-border)',
            backgroundColor: 'var(--theme-card-background)',
            color: 'var(--theme-card-text)',
          }}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          data-testid={`${testId}-chip`}
        >
          <span
            className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full text-[10px] font-bold text-white"
            style={{ backgroundColor: selected ? 'var(--theme-primary, #8b5cf6)' : SLATE }}
          >
            {selected
              ? selected.name.trim().charAt(0).toUpperCase()
              : operators.length > 99 ? '99' : operators.length}
          </span>
          Operadores
        </button>
      </div>

      {/* Bottom-sheet mobile — tema claro fixo; some ≥ sm (desktop usa o facepile) */}
      {sheetOpen && createPortal(
        <div className="sm:hidden fixed inset-0 z-[100]">
          <div
            className="absolute inset-0"
            style={{ backgroundColor: 'rgba(15,23,42,.42)' }}
            onClick={() => setSheetOpen(false)}
            data-testid={`${testId}-sheet-scrim`}
          />
          <div
            className="absolute left-0 right-0 bottom-0 flex flex-col pb-[max(8px,env(safe-area-inset-bottom))]"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '20px 20px 0 0',
              boxShadow: '0 -8px 32px rgba(0,0,0,.16)',
              maxHeight: '74vh',
            }}
            data-testid={`${testId}-sheet`}
          >
            <div className="w-9 h-1 rounded-full mx-auto my-2.5 flex-none" style={{ backgroundColor: '#e2e8f0' }} />
            <div className="flex items-center justify-between px-4 pb-2 flex-none">
              <span className="font-bold text-[16px]" style={{ color: '#1e293b' }}>Filtrar por operador</span>
              {selectedId !== null && (
                <button
                  type="button"
                  onClick={() => pick(null)}
                  className="font-semibold text-[14px]"
                  style={{ color: '#8b5cf6' }}
                  data-testid={`${testId}-sheet-clear`}
                >
                  Limpar
                </button>
              )}
            </div>
            <div className="overflow-y-auto px-2 pb-2">
              {/* "Todos os operadores" */}
              <button
                type="button"
                role="option"
                aria-selected={selectedId === null}
                onClick={() => pick(null)}
                className="w-full flex items-center gap-3 rounded-xl text-left"
                style={{
                  padding: '11px 12px',
                  color: '#334155',
                  backgroundColor: selectedId === null ? '#f3e8ff' : 'transparent',
                }}
                data-testid={`${testId}-sheet-all`}
              >
                <span
                  className="w-7 h-7 flex items-center justify-center text-white flex-none"
                  style={{ backgroundColor: '#8b5cf6', borderRadius: SQUIRCLE }}
                >
                  <Icon name="users" className="w-4 h-4" />
                </span>
                <span className="truncate flex-1 text-[15px] font-medium">Todos os operadores</span>
                {selectedId === null && <Icon name="check" className="w-5 h-5 flex-none" style={{ color: '#8b5cf6' }} />}
              </button>

              {operators.map((op) => (
                <button
                  key={op.id}
                  type="button"
                  role="option"
                  aria-selected={selectedId === op.id}
                  onClick={() => pick(op.id)}
                  className="w-full flex items-center gap-3 rounded-xl text-left"
                  style={{
                    padding: '11px 12px',
                    color: '#334155',
                    backgroundColor: selectedId === op.id ? '#f3e8ff' : 'transparent',
                  }}
                  data-testid={`${testId}-sheet-${op.id}`}
                >
                  <OperatorAvatar operator={op} sizeClass="w-7 h-7" fontSize={11} />
                  <span className="truncate flex-1 text-[15px] font-medium">{op.name}</span>
                  {selectedId === op.id && <Icon name="check" className="w-5 h-5 flex-none" style={{ color: '#8b5cf6' }} />}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

OperatorAvatarFilter.displayName = 'OperatorAvatarFilter';
